import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram, Check } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal } from "./Reveal";
import { DOCTOR_PHOTO, CENTER, INSTAGRAM_URL } from "./data";
import { LaserAttestato } from "./LaserAttestato";
import { DoctorVideo } from "./DoctorVideo";

const SKILLS = [
  "Tutti i protocolli parodontali",
  "Full Mouth Disinfection laser-assistita",
  "Terapia di mantenimento personalizzata",
  "Accoglienza dei pazienti fobici",
];

export const Doctor = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="dottore" ref={ref} className="relative py-28 sm:py-40 bg-[#F4F3EC]" data-testid="doctor-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 grid lg:grid-cols-12 gap-16 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2.5rem] border border-[#1B3B28]/10 rotate-[-2deg]" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] rounded-tl-[9rem]" data-testid="doctor-photo">
              <motion.img src={DOCTOR_PHOTO} alt="Dott. Nicola Mauro" style={{ y, scale: 1.18 }} className="w-full h-full object-cover object-top" />
            </div>
            <DoctorVideo />
            <div className="absolute -bottom-6 -right-2 sm:-right-6 rounded-2xl bg-[#1B3B28] text-[#FCFBF7] px-6 py-5">
              <p className="eyebrow text-[#86C89B]">Opera presso</p>
              <p className="f-serif text-2xl mt-1">{CENTER}</p>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7">
          <SectionHead eyebrow="Chi ti accompagna" lines={["Dott. Nicola", "Mauro"]} />
          <Reveal delay={0.15}>
            <p className="mt-8 text-lg text-[#526356] leading-relaxed max-w-xl">
              Si occupa di tutti i protocolli parodontali, dalla diagnosi al mantenimento, appoggiandosi al {CENTER}. Il suo approccio
              unisce la precisione della tecnologia laser a un'attenzione autentica per le persone — soprattutto per chi entra nello studio con il cuore in gola.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <blockquote className="mt-10 pl-6 border-l-2 border-[#86C89B] f-serif italic text-2xl sm:text-3xl text-[#1B3B28] leading-snug max-w-xl" data-testid="doctor-quote">
              "Salvare un dente naturale comincia sempre da una cosa: far sentire la persona al sicuro."
            </blockquote>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-10 grid sm:grid-cols-2 gap-3 max-w-xl">
              {SKILLS.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-[#1E2922]">
                  <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-[#86C89B]/40 text-[#1B3B28] shrink-0"><Check size={12} strokeWidth={3} /></span>
                  {s}
                </li>
              ))}
            </ul>
            <LaserAttestato />
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="doctor-instagram-link"
              className="mt-8 inline-flex items-center gap-2 text-[#1B3B28] border-b border-[#1B3B28]/30 pb-1 hover:border-[#1B3B28] transition-colors"
            >
              <Instagram size={16} /> @igienistadentalenicola
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
